import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, ChevronLeft, ChevronRight, Expand, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { galleryItems as fallbackGallery } from '../mock';
import { logError } from '../lib/logger';

const GalleryCard = ({ item }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);

  const hasImages = item.images && item.images.length > 0;
  const hasMultipleImages = item.images && item.images.length > 1;

  const nextImage = (e) => {
    e.stopPropagation();
    if (hasImages) {
      setCurrentImageIndex((prev) => (prev + 1) % item.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (hasImages) {
      setCurrentImageIndex((prev) => (prev - 1 + item.images.length) % item.images.length);
    }
  };

  const openLightbox = (index) => {
    setLightboxImageIndex(index);
    setIsLightboxOpen(true);
  };

  const nextLightboxImage = () => {
    if (hasImages) {
      setLightboxImageIndex((prev) => (prev + 1) % item.images.length);
    }
  };

  const prevLightboxImage = () => {
    if (hasImages) {
      setLightboxImageIndex((prev) => (prev - 1 + item.images.length) % item.images.length);
    }
  };

  return (
    <>
      <Card className="bg-slate-800 border-slate-700 overflow-hidden hover:border-gray-400/50 transition-all duration-300">
        {/* Image Carousel */}
        {hasImages ? (
          <div className="relative h-64 bg-slate-900 group">
            <img 
              src={item.images[currentImageIndex]} 
              alt={`${item.title} ${currentImageIndex + 1}`}
              className="w-full h-full object-cover cursor-pointer"
              loading="lazy"
              decoding="async"
              onClick={() => openLightbox(currentImageIndex)}
            />
            
            {/* Expand icon overlay */}
            <div 
              className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              onClick={() => openLightbox(currentImageIndex)}
            >
              <Expand className="w-12 h-12 text-white" />
            </div>

            {/* Navigation arrows - only show if multiple images */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                {/* Image counter */}
                <div className="absolute bottom-2 right-2 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {item.images.length}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="h-64 bg-slate-900 flex items-center justify-center">
            <p className="text-gray-500">Image placeholder</p>
          </div>
        )}
        
        <CardHeader>
          <div className="flex items-start justify-between mb-2">
            <CardTitle className="text-white text-xl">{item.title}</CardTitle>
            <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-600/30">
              {item.category}
            </Badge>
          </div>
          <CardDescription className="text-gray-400">
            {item.description}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Lightbox Modal */}
      <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <DialogContent className="max-w-7xl w-full bg-black/95 border-slate-700 p-0">
          <div className="relative w-full h-[90vh] flex items-center justify-center">
            {/* Close button */}
            <button
              onClick={() => setIsLightboxOpen(false)}
              className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Main image */}
            {hasImages && (
              <>
                <img 
                  src={item.images[lightboxImageIndex]} 
                  alt={`${item.title} ${lightboxImageIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                />

                {/* Navigation arrows for lightbox */}
                {hasMultipleImages && (
                  <>
                    <button
                      onClick={prevLightboxImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all"
                    >
                      <ChevronLeft className="w-8 h-8" />
                    </button>
                    <button
                      onClick={nextLightboxImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all"
                    >
                      <ChevronRight className="w-8 h-8" />
                    </button>

                    {/* Image counter in lightbox */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/10 text-white px-4 py-2 rounded-full text-sm">
                      {lightboxImageIndex + 1} / {item.images.length}
                    </div>
                  </>
                )}

                {/* Image title */}
                <div className="absolute top-4 left-4 bg-black/60 text-white px-4 py-2 rounded-lg">
                  <p className="font-semibold">{item.title}</p>
                  <p className="text-sm text-gray-300">{item.category}</p>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Gallery = () => {
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/gallery`);
        if (!cancelled) setItems(data);
      } catch (err) {
        if (!cancelled) {
          logError('Gallery API failed, using fallback data:', err);
          setError(true);
          setItems(fallbackGallery);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Mount-only fetch; local vars are not reactive deps

  const isLoading = items === null;

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      
      <section className="py-20 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button and header */}
          <div className="mb-12">
            <Link to="/">
              <Button data-testid="gallery-back-home" variant="outline" className="mb-6 bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Work
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl">
              Explore our portfolio of custom builds, restorations, and precision work
            </p>
          </div>

          {/* Gallery Grid */}
          <div data-testid="gallery-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={`gallery-skel-${i}`} data-testid="gallery-skeleton" className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                    <Skeleton className="h-64 w-full bg-slate-700" />
                    <div className="p-6 space-y-3">
                      <Skeleton className="h-5 w-3/4 bg-slate-700" />
                      <Skeleton className="h-4 w-full bg-slate-700" />
                    </div>
                  </div>
                ))
              : items.map((item) => <GalleryCard key={item.id} item={item} />)}
          </div>

          {!isLoading && items.length === 0 && (
            <p data-testid="gallery-empty" className="text-center text-gray-400 py-12">
              No gallery items yet.
            </p>
          )}
          {error && (
            <p className="text-xs text-gray-600 mt-6">Showing cached items — live sync unavailable.</p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Gallery;
